import { alpha, Avatar, Card, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { motion } from "framer-motion";
import { Fragment } from "react";
import { useNavigate } from "react-router-dom";

const StatCardSkeleton = ({ color }) => {
  const theme = useTheme();
  const cardColor = theme.palette[color]?.main || theme.palette.primary.main;

  return (
    <Fragment>
      <Skeleton
        variant="rounded"
        animation="wave"
        sx={{
          height: 56,
          width: 56,
          borderRadius: 2,
          backgroundColor: alpha(cardColor, 0.1),
        }}
      />

      <Stack spacing={1}>
        <Skeleton
          variant="text"
          animation="wave"
          width={80}
          height={40}
          sx={{
            backgroundColor: alpha(cardColor, 0.1),
          }}
        />
        <Skeleton
          variant="text"
          animation="wave"
          width={120}
          height={20}
          sx={{
            backgroundColor: alpha(cardColor, 0.1),
          }}
        />
      </Stack>
    </Fragment>
  );
};

export default function StatCard({ color, icon, label, count, sx, loading = true, href, iconSize = 24 }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const cardColor = theme.palette[color].main;
  
  const handleClick = () => {
    if (href) {
      navigate(href);
    }
  };
  
  return (
    <Card
      component={motion.div}
      whileHover={{ y: -5 }}
      onClick={handleClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 3,
        p: 3,
        height: "100%",
        background: `linear-gradient(135deg, ${alpha(cardColor, 0.1)} 0%, ${alpha(theme.palette.background.paper, 1)} 60%)`,
        borderRadius: 4,
        border: `1px solid ${alpha(cardColor, 0.1)}`,
        transition: "all 0.3s ease-in-out",
        cursor: href ? "pointer" : "default",
        "&:hover": {
          boxShadow: `0 8px 24px ${alpha(cardColor, 0.15)}`,
          ...(href && { transform: "translateY(-8px)" }),
        },
        ...sx,
      }}
    >
      {loading ? (
        <StatCardSkeleton color={cardColor} />
      ) : (
        <Fragment>
          <Avatar
            sx={{
              p: `${(56 - iconSize) / 2}px`,
              borderRadius: 2,
              backgroundColor: alpha(cardColor, 0.1),
              color: cardColor,
              height: 56,
              width: 56,
              "& .MuiSvgIcon-root, & .MuiIcon-root": { fontSize: iconSize },
              "& img": { width: iconSize, height: iconSize, objectFit: "contain" },
            }}
          >
            {icon}
          </Avatar>
          <Stack spacing={1}>
            <Typography variant="h4" fontWeight="bold">
              {count}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {label}
            </Typography>
          </Stack>
        </Fragment>
      )}
    </Card>
  );
}
